/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { validate } from 'app/shared/elements/ess-rule';
import { Sorting, IForm, SortDirection } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { LookupService } from 'app/cd/services/lookup-service';
import { State } from 'app/shared/lib/state';
import { deepClone } from 'app/shared/lib/utilities';
import { MemberService } from 'app/cd/services/member-service';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { SaveEthnicityCustomElement } from 'app/core/components/races-ethnicities/save-ethnicity';
import * as CD from 'app/cd/models/essette-api';


@autoinject()
export class Ethnicities {

    ////****************** Fields and Properties ************************************////

    protected ethnicities: ReadonlyArray<CD.Ethnicity> = [];
    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;
    protected includeInactive = false;
    protected isValid: boolean = false;
    protected validate = validate;
    protected state = new State();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: EssModalCustomElement;
    protected $form!: IForm;
    protected $saveEthnicity!: SaveEthnicityCustomElement;
    protected $ethnicityForm!: Element;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly logger: AppLogger,
        private readonly service: LookupService,
        private readonly cdService: MemberService,
        private readonly toaster: Toaster,) {
        this.logger.source = this;
    }

    protected bind(): void {
        this.loadEthnicities();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'value',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadEthnicities();
    }

    protected addEthnicity() {
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.reset();
        this.$modal.close();
    }

    private editEthnicity(ethnicity: CD.Ethnicity): void {
        if(!ethnicity || !this.$modal) {
            return;
        }
        this.$saveEthnicity.editedEthnicity = deepClone(ethnicity);
        this.$modal.open();
    }

    protected async handleFormSuccess(): Promise<void> {
        this.reset();
        this.loadEthnicities();
        this.$modal.close();
    }

    public reset() {
        this.$saveEthnicity.editedEthnicity = {};
    }

    public async submit(): Promise<boolean> {
        return this.$saveEthnicity.submit();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadEthnicities(): Promise<void> {
        const includes: string[] = [];
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadEthnicities(options, includes);
            this.ethnicities = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateEthnicity(acuity: CD.Ethnicity, isActive: boolean) {
        if(!isActive) {
            const result = await confirmation.showDeactivate('cd:member.ethnicity.$');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.cdService.activateEthnicity(acuity.id!, isActive);
            isSuccessful = response.isSuccessful;
            if(response.isSuccessful) {
                this.loadEthnicities();
            }
        }
        catch(error) {
            this.toaster.standard.activate('cd:member.ethnicity.$', isActive, isSuccessful);
            throw error;
        } finally {
            this.toaster.standard.activate('cd:member.ethnicity.$', isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
